import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useApi } from '../../lib/api';
import { useWorkspace } from '../../lib/workspace';
import { formatHoursMinutes, formatDate } from '../../lib/format';
import { StarRating } from '../../components/workspace/performance/StarRating';
import '../../styles/workspace.css';
import '../../styles/Performance.css';

export default function PerformanceReview() {
  const { appId, reviewId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const api = useApi();
  const { me, canManage, myRole } = useWorkspace();
  const isAdmin = myRole === 'admin' || myRole === 'super_admin';

  const [review, setReview]         = useState(null);
  const [finalApprover, setFinalApprover] = useState(null);
  const [managerName, setManagerName] = useState(null);
  const [finalApproverName, setFinalApproverName] = useState(null);
  const [revieweeName, setRevieweeName] = useState(null);
  const [managerNames, setManagerNames] = useState([]);
  const [finalApproverNames, setFinalApproverNames] = useState([]);
  const [managerUserIds, setManagerUserIds] = useState([]);
  const [managerCandidates, setManagerCandidates] = useState([]);
  const [finalApproverCandidates, setFinalApproverCandidates] = useState([]);
  const [snapshots, setSnapshots]   = useState({ goals: [], skills: [], values: [] });
  const [savedRatings, setSavedRatings] = useState([]);
  const [draft, setDraft]           = useState({});   // { "goal-5": { rating, comments } }
  const [analytics, setAnalytics]   = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [draftSaving, setDraftSaving] = useState(false);
  const [draftSaved, setDraftSaved]   = useState(false);

  // Feedback discussion (manager stage)
  const [feedbackDate, setFeedbackDate]   = useState('');
  const [feedbackNotes, setFeedbackNotes] = useState('');

  // Manager decision
  const [mgrAction, setMgrAction]                     = useState('approve');
  const [mgrRejectionComment, setMgrRejectionComment] = useState('');

  // Final approver decision
  const [finalAction, setFinalAction]   = useState('approve');
  const [finalComment, setFinalComment] = useState('');
  const [hasManager, setHasManager]     = useState(false);

  // Reassignment
  const [reassignType, setReassignType]       = useState(null); // 'manager' | 'final_approver'
  const [reassignUserId, setReassignUserId]   = useState('');
  const [reassigning, setReassigning]         = useState(false);

  // ── Load ─────────────────────────────────────────────────────────────────
  const loadReview = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [reviewData, ratingsData, analyticsData] = await Promise.all([
        api.getPerformanceReview(appId, reviewId),
        api.getReviewRatings(appId, reviewId),
        api.getReviewAnalytics(appId, reviewId),
      ]);

      const rev = reviewData.review;
      setReview(rev);
      setFinalApprover(reviewData.finalApprover || null);
      setManagerName(reviewData.managerName || null);
      setFinalApproverName(reviewData.finalApproverName || null);
      setRevieweeName(reviewData.revieweeName || null);
      setManagerNames(reviewData.managerNames || []);
      setFinalApproverNames(reviewData.finalApproverNames || []);
      setManagerUserIds(reviewData.managerUserIds || []);
      setManagerCandidates(reviewData.managerCandidates || []);
      setFinalApproverCandidates(reviewData.finalApproverCandidates || []);
      setHasManager(reviewData.has_manager || false);
      setSnapshots(reviewData.snapshots || { goals: [], skills: [], values: [] });
      setSavedRatings(ratingsData || []);

      // Pre-populate draft with saved ratings for the active rater role (including drafts)
      const raterRole = rev.status === 'pending_self'     ? 'self'
                      : rev.status === 'pending_manager'  ? 'manager'
                      : rev.status === 'pending_approver' ? 'final_approver'
                      : null;
      if (raterRole && raterRole !== 'final_approver') {
        const initDraft = {};
        (ratingsData || [])
          .filter(r => r.rater_role === raterRole)
          .forEach(r => {
            initDraft[`${r.rating_type}-${r.snapshot_id}`] = {
              rating: r.rating || 0,
              comments: r.comments || '',
            };
          });
        setDraft(initDraft);
      }

      // Restore final approver draft action/comment
      if (rev.draft_final_action) setFinalAction(rev.draft_final_action);
      if (rev.draft_final_comment) setFinalComment(rev.draft_final_comment);

      // Pre-populate feedback discussion date/notes if already saved (e.g. re-review after rejection)
      if (reviewData.feedbackDiscussion) {
        const dd = reviewData.feedbackDiscussion.discussion_date;
        setFeedbackDate(dd ? dd.slice(0, 10) : '');
        setFeedbackNotes(reviewData.feedbackDiscussion.discussion_notes || '');
      }

      setAnalytics(analyticsData);
    } catch {
      setError('Failed to load review');
    } finally {
      setLoading(false);
    }
  }, [api, appId, reviewId]);

  useEffect(() => { loadReview(); }, [loadReview]);

  const handleReassign = async () => {
    if (!reassignUserId) return;
    const label = reassignType === 'manager' ? 'manager' : 'final approver';
    const extra = reassignType === 'manager'
      ? '\n\nWarning: existing manager ratings will be cleared so the new manager can start fresh.'
      : '';
    if (!window.confirm(`Reassign the ${label} to the selected person?${extra}`)) return;
    setReassigning(true);
    try {
      await api.reassignReview(appId, reviewId, { type: reassignType, new_user_id: Number(reassignUserId) });
      setReassignType(null);
      setReassignUserId('');
      await loadReview();
    } catch (err) {
      setError(err.response?.data?.error || 'Reassignment failed');
    } finally {
      setReassigning(false);
    }
  };

  // Auto-trigger print when ?print=1 is in the URL (used by PDF button)
  useEffect(() => {
    if (!loading && review && new URLSearchParams(location.search).get('print') === '1') {
      setTimeout(() => window.print(), 300);
    }
  }, [loading, review, location.search]);

  // ── Draft helpers ─────────────────────────────────────────────────────────
  const savedFor = (type, snapshotId, role) =>
    savedRatings.find(
      r => r.rating_type === type && r.snapshot_id === snapshotId && r.rater_role === role
    );

  const getDraft = (type, snapshotId) =>
    draft[`${type}-${snapshotId}`] || { rating: 0, comments: '' };

  const setDraftItem = (type, snapshotId, rating, comments) =>
    setDraft(prev => ({ ...prev, [`${type}-${snapshotId}`]: { rating, comments } }));

  const buildPayload = () =>
    Object.entries(draft)
      .map(([key, val]) => {
        const idx = key.indexOf('-');
        return {
          type: key.slice(0, idx),
          snapshot_id: Number(key.slice(idx + 1)),
          rating: val.rating,
          comments: val.comments || '',
        };
      })
      .filter(r => r.rating > 0);

  // ── Aggregate averages ────────────────────────────────────────────────────
  // Weighted average for goals (role = 'self'|'manager'|'final_approver', or null = use draft)
  const goalWeightedAvg = (role) => {
    const goals = snapshots.goals || [];
    const totalW = goals.reduce((s, g) => s + (g.weightage || 0), 0);
    if (!totalW || !goals.length) return null;
    let weighted = 0, hasAny = false;
    for (const g of goals) {
      const r = role ? savedFor('goal', g.id, role) : getDraft('goal', g.id);
      const rating = r?.rating || 0;
      if (rating > 0) { weighted += (g.weightage || 0) * rating; hasAny = true; }
    }
    return hasAny ? weighted / totalW : null;
  };

  // Simple average for skills / values
  // snapshots keys are plural ('skills','values') but draft keys and rating_type in DB are singular ('skill','value')
  const singularType = (type) => type === 'skills' ? 'skill' : type === 'values' ? 'value' : type;

  const simpleAvg = (type, role) => {
    const items = snapshots[type] || [];
    if (!items.length) return null;
    const sType = singularType(type);
    const vals = items
      .map(item => {
        const r = role ? savedFor(sType, item.id, role) : getDraft(sType, item.id);
        return r?.rating || 0;
      })
      .filter(r => r > 0);
    return vals.length ? vals.reduce((s, r) => s + r, 0) / vals.length : null;
  };

  // ── Role flags ─────────────────────────────────────────────────────────────
  const isReviewee            = review?.platform_user_id === me?.id;
  const canSelfRate           = review?.status === 'pending_self'     && isReviewee;
  const canManagerRate        = review?.status === 'pending_manager'  && !isReviewee
    && managerUserIds.map(Number).includes(me?.id);
  const isDesignatedApprover  = finalApprover
    ? (finalApprover.user_ids || []).includes(me?.id)
    : canManage;
  const canFinalRate          = review?.status === 'pending_approver' && isDesignatedApprover;

  // If manager and final approver are the same person(s), hide the final approver section entirely
  const finalApproverIds = (finalApprover?.user_ids || []).map(Number);
  const managerAndApproverSame = managerUserIds.length > 0 && finalApproverIds.length > 0
    && managerUserIds.map(Number).every(id => finalApproverIds.includes(id))
    && finalApproverIds.every(id => managerUserIds.map(Number).includes(id));

  // Which rows to render per item
  const showSelf    = true;
  // Employees must not see manager ratings until final approval is complete
  const showManager = !isReviewee
    ? ['pending_manager', 'pending_approver', 'approved', 'rejected'].includes(review?.status)
    : ['approved', 'rejected'].includes(review?.status);
  const showFinal   = !managerAndApproverSame && (canFinalRate || ['approved', 'rejected'].includes(review?.status));

  // ── Submit handlers ────────────────────────────────────────────────────────
  const handleSubmitSelf = async () => {
    if (!window.confirm('Submit your self-rating for manager review?')) return;
    setSubmitting(true);
    try {
      const updated = await api.submitSelfRating(appId, reviewId, { ratings: buildPayload() });
      if (updated?.id) setReview(updated);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit');
    } finally {
      await loadReview();
      setSubmitting(false);
    }
  };

  const handleSubmitManager = async () => {
    if (mgrAction === 'approve' && !feedbackDate) {
      setError('Feedback discussion date is required');
      return;
    }
    if (mgrAction === 'reject' && !mgrRejectionComment.trim()) {
      setError('Rejection comment is required');
      return;
    }
    const confirmMsg = mgrAction === 'approve'
      ? 'Submit your manager review and send for final approval?'
      : 'Reject this review and send it back to the employee for revision?';
    if (!window.confirm(confirmMsg)) return;
    setSubmitting(true);
    try {
      const updated = await api.submitManagerRating(appId, reviewId, {
        action: mgrAction,
        ratings: mgrAction === 'approve' ? buildPayload() : [],
        feedback_discussion_date: mgrAction === 'approve' ? feedbackDate : undefined,
        discussion_notes: mgrAction === 'approve' ? feedbackNotes : undefined,
        rejection_comment: mgrAction === 'reject' ? mgrRejectionComment : undefined,
      });
      if (updated?.id) setReview(updated);
      // Reload ratings so submitted scores appear in read-only view
      try {
        const ratingsData = await api.getReviewRatings(appId, reviewId);
        setSavedRatings(ratingsData || []);
      } catch (_) {}
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit');
      await loadReview();
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitFinal = async () => {
    if (finalAction !== 'approve' && !finalComment.trim()) {
      setError('Comment is required when rejecting');
      return;
    }
    const confirmMessages = {
      approve:            'Approve this review? This will complete the workflow.',
      reject_to_manager:  'Reject and send back to manager for revision?',
      reject_to_employee: 'Reject and send back to the employee for revision?',
    };
    if (!window.confirm(confirmMessages[finalAction])) return;
    setSubmitting(true);
    try {
      const updated = await api.submitFinalRating(appId, reviewId, {
        final_action: finalAction,
        final_comment: finalComment,
      });
      if (updated?.id) setReview(updated);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit');
    } finally {
      await loadReview();
      setSubmitting(false);
    }
  };

  const buildDraftPayload = () =>
    Object.entries(draft).map(([key, val]) => {
      const idx = key.indexOf('-');
      return { type: key.slice(0, idx), snapshot_id: Number(key.slice(idx + 1)), rating: val.rating || null, comments: val.comments || '' };
    });

  const handleSaveDraft = async (raterRole) => {
    setDraftSaving(true);
    setDraftSaved(false);
    try {
      if (raterRole === 'final_approver') {
        await api.saveDraft(appId, reviewId, { rater_role: 'final_approver', final_action: finalAction, final_comment: finalComment });
      } else {
        await api.saveDraft(appId, reviewId, { rater_role: raterRole, ratings: buildDraftPayload(), feedback_date: feedbackDate || null, feedback_notes: feedbackNotes || null });
      }
      setDraftSaved(true);
      setTimeout(() => setDraftSaved(false), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save draft');
    } finally {
      setDraftSaving(false);
    }
  };

  // ── Early returns ──────────────────────────────────────────────────────────
  if (loading) return <div className="prv-loading">Loading review…</div>;
  if (!review) return <div className="prv-error-page">Review not found</div>;

  // ── Display helpers ────────────────────────────────────────────────────────
  const fmtAvg = (n) => (n != null ? n.toFixed(2) : '—');

  const STATUS_LABEL = {
    pending_self:     'Pending Self-Rating',
    pending_manager:  'Pending Manager Review',
    pending_approver: 'Pending Final Approval',
    approved:         'Approved',
    rejected:         'Rejected',
    closed:           'Closed',
  };

  // Live averages — if the user is actively editing, show draft averages; else show saved
  const selfGoalAvg  = canSelfRate ? goalWeightedAvg(null)    : goalWeightedAvg('self');
  const selfSkillAvg = canSelfRate ? simpleAvg('skills', null) : simpleAvg('skills', 'self');
  const selfValueAvg = canSelfRate ? simpleAvg('values', null) : simpleAvg('values', 'self');
  const mgrGoalAvg   = canManagerRate ? goalWeightedAvg(null)    : goalWeightedAvg('manager');
  const mgrSkillAvg  = canManagerRate ? simpleAvg('skills', null) : simpleAvg('skills', 'manager');
  const mgrValueAvg  = canManagerRate ? simpleAvg('values', null) : simpleAvg('values', 'manager');

  // Hide manager scores from employee until approved/rejected
  const showMgrScores = !isReviewee || ['approved', 'rejected'].includes(review?.status);

  // ── Render a single rater row (inline helper, not a React component) ───────
  const renderRatingRow = (type, item, role, label, editable) => {
    const saved   = savedFor(type, item.id, role);
    const d       = getDraft(type, item.id);
    const curStar = editable ? d.rating   : (saved?.rating   || 0);
    const curNote = editable ? d.comments : (saved?.comments || '');

    // For member (isReviewee) read-only non-self rows: hide if no data yet
    if (!editable && isReviewee && role !== 'self' && !saved) return null;

    // Member sees text-only (no star widget) for read-only rows
    if (!editable && isReviewee) {
      return (
        <div key={role} className="prv-rater-row">
          <div className="prv-rater-meta">
            <span className="prv-rater-label">{label}</span>
            {curStar > 0 ? (
              <span className="prv-saved-stars-label" style={{ fontWeight: 600, fontSize: 14 }}>
                {curStar}/5
              </span>
            ) : (
              <span className="prv-saved-stars-label" style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
                Not Yet Rated
              </span>
            )}
          </div>
          {curNote ? <p className="prv-saved-comment">"{curNote}"</p> : null}
        </div>
      );
    }

    return (
      <div key={role} className="prv-rater-row">
        <div className="prv-rater-meta">
          <span className="prv-rater-label">{label}</span>
          <StarRating
            value={curStar}
            onChange={editable ? (r) => setDraftItem(type, item.id, r, d.comments) : undefined}
            readOnly={!editable}
            size="sm"
          />
          {curStar > 0 && !editable && (
            <span className="prv-saved-stars-label">{curStar}/5</span>
          )}
        </div>
        {editable ? (
          <textarea
            className="prv-comment-input"
            value={d.comments}
            onChange={e => setDraftItem(type, item.id, d.rating, e.target.value)}
            placeholder="Add comments… (optional)"
            maxLength={1000}
            rows={2}
          />
        ) : curNote ? (
          <p className="prv-saved-comment">"{curNote}"</p>
        ) : null}
      </div>
    );
  };

  // ── Render an item card (goal / skill / value) ─────────────────────────────
  const renderItem = (type, item) => (
    <div key={item.id} className="prv-item">
      {/* Item header */}
      <div className="prv-item-head">
        <span className="prv-item-name">
          {item.goal_name || item.skill_name || item.value_name}
        </span>
        {item.weightage != null && (
          <span className="prv-weightage-badge">{item.weightage}%</span>
        )}
        {item.is_mandatory ? (
          <span className="prv-mandatory-badge">Mandatory</span>
        ) : null}
      </div>

      {/* Goal targets (compact chips) */}
      {type === 'goal' &&
        (item.target_l1 || item.target_l2 || item.target_l3 || item.target_l4 || item.target_l5) && (
          <div className="prv-targets">
            {['l1', 'l2', 'l3', 'l4', 'l5'].map(
              l =>
                item[`target_${l}`] && (
                  <span key={l} className="prv-target-chip">
                    <strong>{l.toUpperCase()}:</strong> {item[`target_${l}`]}
                  </span>
                )
            )}
          </div>
        )}

      {/* Rating rows */}
      <div className="prv-ratings-block">
        {showSelf    && renderRatingRow(type, item, 'self',    'Self',    canSelfRate)}
        {showManager && renderRatingRow(type, item, 'manager', managerName ? `Manager (${managerName})` : 'Manager', canManagerRate)}
      </div>
    </div>
  );

  // ── JSX ───────────────────────────────────────────────────────────────────
  return (
    <div className="prv-page">
      <button className="btn-back" onClick={() => navigate(-1)}>←</button>
      {/* ── Header ── */}
      <div className="prv-header">
        <div>
          <h1 className="page-title">Performance Review{revieweeName ? ` — ${revieweeName}` : ''}</h1>
          {review.cycle_name && (
            <p className="prv-subtitle">
              {review.cycle_name}
              {review.start_date && review.end_date &&
                ` (${formatDate(review.start_date)} – ${formatDate(review.end_date)})`}
            </p>
          )}
        </div>
        <span className={`prv-status-pill prv-status-${review.status}`}>
          {STATUS_LABEL[review.status] || review.status}
        </span>
      </div>

      {/* ── Manager & Approver info strip ── */}
      {(managerNames.length > 0 || finalApproverNames.length > 0) && (
        <div className="prv-people-strip">
          {managerNames.length > 0 && (
            <div className="prv-people-item">
              <span className="prv-people-label">Manager</span>
              <span className="prv-people-value">{managerNames.join(', ')}</span>
            </div>
          )}
          {!managerAndApproverSame && finalApproverNames.length > 0 && (
            <div className="prv-people-item">
              <span className="prv-people-label">Final Approver</span>
              <span className="prv-people-value">{finalApproverNames.join(', ')}</span>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="prv-error-banner">
          {error}
          <button className="prv-banner-close" onClick={() => setError('')}>×</button>
        </div>
      )}

      {review.status === 'closed' && (
        <div className="prv-info-banner prv-info-banner--returned">
          This review was closed{review.closed_at ? ` on ${formatDate(review.closed_at)}` : ''} and can no longer be edited.
          {review.close_reason && <> Reason: <em>{review.close_reason}</em></>}
        </div>
      )}

      {/* Returned-for-revision banners */}
      {isReviewee && review.status === 'pending_self' && review.rejection_target === 'self' && (
        <div className="prv-info-banner prv-info-banner--returned">
          <strong>Your review was returned for revision.</strong> Please update your self-rating and resubmit.
          {review.rejection_reason && <> Reason: <em>{review.rejection_reason}</em></>}
        </div>
      )}
      {!isReviewee && review.status === 'pending_manager' && review.rejection_target === 'manager' && (
        <div className="prv-info-banner prv-info-banner--returned">
          <strong>This review was returned by the final approver for your revision.</strong> Please update your manager ratings and resubmit.
          {review.rejection_reason && <> Reason: <em>{review.rejection_reason}</em></>}
        </div>
      )}

      {isReviewee && review.status === 'pending_manager' && !review.rejection_target && (
        <div className="prv-info-banner">
          Your self-rating has been submitted. Awaiting manager review.
        </div>
      )}
      {isReviewee && review.status === 'pending_approver' && (
        <div className="prv-info-banner">
          {managerAndApproverSame
            ? 'Manager review is complete. Awaiting final approval from manager.'
            : 'Manager review is complete. Awaiting final approval.'}
        </div>
      )}
      {isReviewee && review.status === 'approved' && (
        <div className="prv-info-banner" style={{ background: 'var(--green-bg, #e6f9ed)', color: 'var(--green-text, #1a7a3c)', borderColor: 'var(--green-border, #7ed4a1)' }}>
          Your performance review has been approved.
        </div>
      )}
      {isReviewee && review.status === 'rejected' && (
        <div className="prv-info-banner" style={{ background: 'var(--red-bg, #fdecea)', color: 'var(--red-text, #b91c1c)', borderColor: 'var(--red-border, #f9a8a8)' }}>
          Your performance review was not approved.
          {review.rejection_reason && <> Reason: {review.rejection_reason}</>}
        </div>
      )}
      {!isReviewee && review.status === 'pending_approver' && !canFinalRate && !managerAndApproverSame && (
        <div className="prv-info-banner">
          Awaiting final approval from the designated approver.
        </div>
      )}

      {/* ── Summary score bar ── */}
      <div className="prv-summary-bar">
        <div className="prv-score-card">
          <span className="prv-score-label">Goals (Weighted Avg)</span>
          <div className="prv-score-values">
            <span className="prv-score-self">{fmtAvg(selfGoalAvg)}<small>/5</small></span>
            {showMgrScores && mgrGoalAvg != null && (
              <span className="prv-score-mgr">{fmtAvg(mgrGoalAvg)}<small> mgr</small></span>
            )}
          </div>
        </div>
        <div className="prv-score-card">
          <span className="prv-score-label">Skills (Simple Avg)</span>
          <div className="prv-score-values">
            <span className="prv-score-self">{fmtAvg(selfSkillAvg)}<small>/5</small></span>
            {showMgrScores && mgrSkillAvg != null && (
              <span className="prv-score-mgr">{fmtAvg(mgrSkillAvg)}<small> mgr</small></span>
            )}
          </div>
        </div>
        <div className="prv-score-card">
          <span className="prv-score-label">Values (Simple Avg)</span>
          <div className="prv-score-values">
            <span className="prv-score-self">{fmtAvg(selfValueAvg)}<small>/5</small></span>
            {showMgrScores && mgrValueAvg != null && (
              <span className="prv-score-mgr">{fmtAvg(mgrValueAvg)}<small> mgr</small></span>
            )}
          </div>
        </div>
      </div>

      {/* ── Main layout ── */}
      <div className="prv-layout">
        <div className="prv-main">

          {/* Goals section */}
          {(snapshots.goals || []).length > 0 && (
            <div className="prv-section">
              <div className="prv-section-head">
                <h2 className="prv-section-title">Goals</h2>
                <div className="prv-section-avgs">
                  <span className="prv-avg-chip self">Self {fmtAvg(selfGoalAvg)}/5</span>
                  {showMgrScores && mgrGoalAvg != null && (
                    <span className="prv-avg-chip mgr">Mgr {fmtAvg(mgrGoalAvg)}/5</span>
                  )}
                </div>
              </div>
              {snapshots.goals.map(g => renderItem('goal', g))}
            </div>
          )}

          {/* Skills section */}
          {(snapshots.skills || []).length > 0 && (
            <div className="prv-section">
              <div className="prv-section-head">
                <h2 className="prv-section-title">Skills</h2>
                <div className="prv-section-avgs">
                  <span className="prv-avg-chip self">Self {fmtAvg(selfSkillAvg)}/5</span>
                  {showMgrScores && mgrSkillAvg != null && (
                    <span className="prv-avg-chip mgr">Mgr {fmtAvg(mgrSkillAvg)}/5</span>
                  )}
                </div>
              </div>
              {snapshots.skills.map(s => renderItem('skill', s))}
            </div>
          )}

          {/* Values section */}
          {(snapshots.values || []).length > 0 && (
            <div className="prv-section">
              <div className="prv-section-head">
                <h2 className="prv-section-title">Company Values</h2>
                <div className="prv-section-avgs">
                  <span className="prv-avg-chip self">Self {fmtAvg(selfValueAvg)}/5</span>
                  {showMgrScores && mgrValueAvg != null && (
                    <span className="prv-avg-chip mgr">Mgr {fmtAvg(mgrValueAvg)}/5</span>
                  )}
                </div>
              </div>
              {snapshots.values.map(v => renderItem('value', v))}
            </div>
          )}

          {/* Final approver comment — styled like Self/Manager rater rows */}
          {!managerAndApproverSame && review.final_comment && !canFinalRate && (
            <div className="prv-item">
              <div className="prv-ratings-block">
                <div className="prv-rater-row">
                  <div className="prv-rater-meta">
                    <span className="prv-rater-label">
                      Final Approver{finalApproverName ? ` (${finalApproverName})` : ''}
                    </span>
                  </div>
                  <p className="prv-saved-comment">"{review.final_comment}"</p>
                </div>
              </div>
            </div>
          )}

          {/* Meeting discussion — hide when review is rejected or employee hasn't been approved yet */}
          {!canManagerRate && feedbackDate && review.status !== 'rejected' && (!isReviewee || ['approved'].includes(review.status)) && (
            <div className="prv-section prv-meeting-section">
              <div className="prv-meeting-header">
                <span className="prv-meeting-icon">📅</span>
                <div>
                  <h2 className="prv-section-title" style={{ margin: 0 }}>Meeting Discussion</h2>
                  <p className="prv-meeting-subtitle">Conducted by manager</p>
                </div>
              </div>
              <div className="prv-meeting-details">
                <div className="prv-meeting-detail-item">
                  <span className="prv-meeting-detail-label">Date</span>
                  <span className="prv-meeting-detail-value">
                    {new Date(feedbackDate).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                  </span>
                </div>
                {feedbackNotes && (
                  <div className="prv-meeting-detail-item prv-meeting-notes">
                    <span className="prv-meeting-detail-label">Notes</span>
                    <p className="prv-meeting-detail-value prv-meeting-notes-text">{feedbackNotes}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Manager decision section */}
          {canManagerRate && (
            <div className="prv-section">
              <h2 className="prv-section-title" style={{ marginBottom: 15 }}>Manager Decision</h2>
              <div className="prv-feedback-block">
                <div className="prv-fb-field">
                  <label style={{ fontWeight: 600, fontSize: 13 }}>Action</label>
                  <select
                    value={mgrAction}
                    onChange={e => setMgrAction(e.target.value)}
                    className="prv-final-select"
                  >
                    <option value="approve">Approve — send for final approval</option>
                    <option value="reject">Reject — send back to employee</option>
                  </select>
                </div>

                {mgrAction === 'approve' && (
                  <>
                    <div className="prv-fb-field">
                      <label>
                        Discussion Date <span style={{ color: '#e74c3c' }}>*</span>
                      </label>
                      <input
                        type="date"
                        value={feedbackDate}
                        onChange={e => setFeedbackDate(e.target.value)}
                        className="prv-date-input"
                      />
                    </div>
                    <div className="prv-fb-field">
                      <label>Notes</label>
                      <textarea
                        value={feedbackNotes}
                        onChange={e => setFeedbackNotes(e.target.value)}
                        maxLength={1000}
                        rows={3}
                        className="prv-comment-input"
                        placeholder="Key points from the discussion…"
                      />
                    </div>
                  </>
                )}

                {mgrAction === 'reject' && (
                  <div className="prv-fb-field">
                    <label>
                      Rejection Reason <span style={{ color: '#e74c3c' }}>*</span>
                    </label>
                    <textarea
                      value={mgrRejectionComment}
                      onChange={e => setMgrRejectionComment(e.target.value)}
                      maxLength={1000}
                      rows={3}
                      className="prv-comment-input"
                      placeholder="Explain why the review is being returned to the employee…"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Submit area */}
          {(canSelfRate || canManagerRate || canFinalRate) && (
            <div className="prv-actions">
              {draftSaved && <span className="prv-draft-saved">Draft saved</span>}
              {canSelfRate && (
                <div className="prv-action-row">
                  <button
                    className="btn-secondary"
                    onClick={() => handleSaveDraft('self')}
                    disabled={submitting || draftSaving}
                  >
                    {draftSaving ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    className="btn-primary"
                    onClick={handleSubmitSelf}
                    disabled={submitting || buildPayload().length === 0}
                  >
                    {submitting ? 'Submitting…' : 'Submit Self-Rating'}
                  </button>
                </div>
              )}
              {canManagerRate && (
                <div className="prv-action-row">
                  {mgrAction === 'approve' && (
                    <button
                      className="btn-secondary"
                      onClick={() => handleSaveDraft('manager')}
                      disabled={submitting || draftSaving}
                    >
                      {draftSaving ? 'Saving…' : 'Save'}
                    </button>
                  )}
                  <button
                    className="btn-primary"
                    style={mgrAction === 'reject' ? { background: '#e74c3c' } : {}}
                    onClick={handleSubmitManager}
                    disabled={submitting || (mgrAction === 'approve' && !feedbackDate) || (mgrAction === 'reject' && !mgrRejectionComment.trim())}
                  >
                    {submitting ? 'Submitting…' : mgrAction === 'approve' ? 'Submit Manager Review' : 'Reject & Return to Employee'}
                  </button>
                </div>
              )}
              {canFinalRate && (
                <div className="prv-final-row">
                  <label style={{ fontWeight: 600, fontSize: 13 }}>Final Decision</label>
                  <select
                    value={finalAction}
                    onChange={e => setFinalAction(e.target.value)}
                    className="prv-final-select"
                  >
                    <option value="approve">Approve</option>
                    <option value="reject_to_manager" disabled={!hasManager}>
                      Reject — send back to Manager{!hasManager ? ' (no manager assigned)' : ''}
                    </option>
                    <option value="reject_to_employee">Reject — send back to Employee</option>
                  </select>
                  <label style={{ fontWeight: 600, fontSize: 13 }}>Notes</label>
                  <textarea
                    className="prv-comment-input"
                    placeholder={finalAction === 'approve' ? 'Approval notes (optional)…' : 'Reason for rejection (required)…'}
                    value={finalComment}
                    onChange={e => setFinalComment(e.target.value.slice(0, 1000))}
                    maxLength={1000}
                    rows={2}
                  />
                  <span style={{ fontSize: 11, color: finalComment.length >= 1000 ? 'var(--danger)' : 'var(--text-muted)' }}>
                    {finalComment.length}/1000
                  </span>
                  <div className="prv-action-row">
                    <button
                      className="btn-secondary"
                      onClick={() => handleSaveDraft('final_approver')}
                      disabled={submitting || draftSaving}
                    >
                      {draftSaving ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      className="btn-primary"
                      style={finalAction !== 'approve' ? { background: '#e74c3c' } : {}}
                      onClick={handleSubmitFinal}
                      disabled={submitting || (finalAction !== 'approve' && !finalComment.trim())}
                    >
                      {submitting
                        ? 'Submitting…'
                        : finalAction === 'approve'
                        ? 'Approve Review'
                        : 'Reject Review'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Sidebar: analytics detail ── */}
        <div className="prv-sidebar">
          <h3>Performance Metrics</h3>
          {analytics ? (
            <>
              {[
                { label: 'Tasks Done',       value: analytics.tasksCompleted,          icon: '✅', sub: 'completed tasks' },
                { label: 'Hours Logged',      value: formatHoursMinutes(analytics.hoursSpentMinutes ?? 0),      icon: '⏱️', sub: 'total time tracked' },
                { label: 'Avg Daily Hours',   value: formatHoursMinutes(analytics.avgDailyMinutes ?? 0),        icon: '📅', sub: 'per working day' },
                { label: 'Routines Done',     value: analytics.routinesCompleted != null ? `${analytics.routinesCompleted}/${analytics.routinesTotal}` : '—', icon: '🔄', sub: 'routine activities' },
                { label: 'Biz Reviews',       value: analytics.businessReviewsDone ?? 0, icon: '🤝', sub: `${analytics.dailyReviewsDone ?? 0} daily · ${analytics.otherReviewsDone ?? 0} other` },
              ].map(({ label, value, icon, sub }) => (
                <div key={label} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 0', borderBottom:'1px solid var(--border)' }}>
                  <span style={{ fontSize:18, flexShrink:0, width:24, textAlign:'center' }}>{icon}</span>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:11, color:'var(--text-muted)', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.04em' }}>{label}</div>
                    <div style={{ fontSize:15, fontWeight:800, color:'var(--text-primary)', lineHeight:1.3 }}>{value}</div>
                    <div style={{ fontSize:11, color:'var(--text-muted)' }}>{sub}</div>
                  </div>
                </div>
              ))}
              {analytics.overdueTasks > 0 && (
                <div style={{ marginTop:10, padding:'8px 10px', borderRadius:7, background:'rgba(255,107,107,0.1)', border:'1px solid rgba(255,107,107,0.3)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                  <span style={{ fontSize:12, color:'#ff6b6b', fontWeight:600 }}>⚠️ Overdue Tasks</span>
                  <strong style={{ color:'#ff6b6b' }}>{analytics.overdueTasks}</strong>
                </div>
              )}
              {analytics.avgRatings?.length > 0 && showMgrScores && (
                <>
                  <h3 style={{ marginTop:18, marginBottom:6 }}>Avg By Rater</h3>
                  {[...analytics.avgRatings]
                    .sort((a, b) => {
                      const order = ['self', 'manager', 'final_approver'];
                      return order.indexOf(a.rater_role) - order.indexOf(b.rater_role);
                    })
                    .map(ar => {
                      const raterLabel = { self: 'Self', manager: 'Manager', final_approver: 'Final Approver' }[ar.rater_role] || ar.rater_role;
                      const typeLabel = ar.rating_type ? ar.rating_type.charAt(0).toUpperCase() + ar.rating_type.slice(1) : '';
                      return (
                        <div key={`${ar.rating_type}-${ar.rater_role}`} className="prv-metric-row">
                          <span>{raterLabel}{typeLabel ? ` · ${typeLabel}` : ''}</span>
                          <strong>{ar.avg_rating?.toFixed(2)}/5</strong>
                        </div>
                      );
                    })}
                </>
              )}
            </>
          ) : (
            <div style={{ color:'var(--text-muted)', fontSize:13, padding:'12px 0' }}>Loading metrics…</div>
          )}
        </div>
      </div>
    </div>
  );
}
